import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { fetchProduct, fetchSocialProof, createCheckoutSession, trackProductVisit } from '@/lib/api';
import { trackProductClick, trackBuyNowClick, trackPurchase, trackAbandonedCart, trackPromoCodeCopy, trackRedirectToMainSite } from '@/lib/tracking';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ShoppingBag, BookOpen, ArrowLeft, Copy, Check, Tag, Clock, Star, TrendingUp, ExternalLink, CreditCard, Mail, MapPin, Truck, User, Settings, LogOut, Quote } from 'lucide-react';

function useCountdown(targetDate) {
  const calc = useCallback(() => {
    if (!targetDate) return null;
    const diff = new Date(targetDate) - new Date();
    if (diff <= 0) return null;
    return { days: Math.floor(diff / 86400000), hours: Math.floor((diff / 3600000) % 24), minutes: Math.floor((diff / 60000) % 60), seconds: Math.floor((diff / 1000) % 60) };
  }, [targetDate]);
  const [time, setTime] = useState(calc);
  useEffect(() => { const id = setInterval(() => setTime(calc()), 1000); return () => clearInterval(id); }, [calc]);
  return time;
}

export default function ProductLanding() {
  const { campaignId } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [socialProof, setSocialProof] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState(1);
  const [promoInput, setPromoInput] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [billingForm, setBillingForm] = useState({ name: '', email: '', tax_code: '', address: '', city: '', zip: '', country: 'IT' });
  const [shippingForm, setShippingForm] = useState({ name: '', address: '', city: '', zip: '', country: 'IT' });
  const [deliveryEmail, setDeliveryEmail] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [billingType, setBillingType] = useState('ricevuta');
  const [invoiceForm, setInvoiceForm] = useState({ company_name: '', vat_number: '', sdi_code: '' });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchProduct(campaignId);
        setProduct(res.data);
        trackProductClick(campaignId, res.data.title);
        try { const sp = await fetchSocialProof(campaignId); setSocialProof(sp.data); } catch {}
      } catch { setNotFound(true); }
      setLoading(false);
    })();
  }, [campaignId]);

  // Track visit for logged-in users → triggers MailerLite email
  useEffect(() => {
    if (product && user) {
      trackProductVisit(campaignId, window.location.href);
    }
  }, [product, user, campaignId]);

  const countdown = useCountdown(product?.active_until);
  const isActive = product?.is_currently_active;
  const computeDiscounted = (p) => {
    if (!p) return null;
    const dv = p.promo_discount_value || 0;
    if (dv <= 0) return p.price.toFixed(2);
    const dt = p.promo_discount_type || 'percentage';
    const amount = dt === 'fixed' ? Math.min(dv, p.price) : (p.price * dv) / 100;
    return Math.max(0, p.price - amount).toFixed(2);
  };
  const discountedPrice = computeDiscounted(product);
  const features = product?.features ? product.features.split('\n').filter(f => f.trim()) : [];

  const copyPromo = () => {
    if (!product?.promo_code) return;
    navigator.clipboard.writeText(product.promo_code);
    setCopied(true); trackPromoCodeCopy(campaignId, product.promo_code);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Codice copiato!');
  };

  const handleBuyNow = () => {
    trackBuyNowClick(campaignId, product.title, product.price);
    setPromoInput(product.promo_code || '');
    setCheckoutStep(1);
    setBillingForm(p => ({ ...p, email: user?.email || '', name: user?.name || '' }));
    setDeliveryEmail(user?.email || '');
    setCheckoutOpen(true);
  };

  const handleCheckout = async () => {
    if (!billingForm.name || !billingForm.email) { toast.error('Compila nome e email'); return; }
    const tc = (billingForm.tax_code || '').replace(/\s/g, '').toUpperCase();
    if (!tc) { toast.error('Codice Fiscale obbligatorio'); return; }
    if (tc.length !== 16 && tc.length !== 11) {
      toast.error('Codice Fiscale non valido (16 caratteri per persona fisica, 11 cifre per P.IVA)');
      return;
    }
    if (!product.is_digital && (!shippingForm.address || !shippingForm.city)) { toast.error('Compila indirizzo spedizione'); return; }
    if (product.is_digital && !deliveryEmail) { toast.error('Inserisci email consegna'); return; }
    setCheckoutLoading(true);
    const wantsInvoice = !!(invoiceForm.vat_number || invoiceForm.sdi_code || invoiceForm.company_name);
    try {
      const res = await createCheckoutSession({
        campaign_id: campaignId, promo_code: promoInput, payment_method: paymentMethod,
        origin_url: window.location.origin, billing_name: billingForm.name, billing_email: billingForm.email,
        billing_address: billingForm.address, billing_city: billingForm.city, billing_zip: billingForm.zip, billing_country: billingForm.country,
        tax_code: tc,
        shipping_name: shippingForm.name || billingForm.name, shipping_address: shippingForm.address,
        shipping_city: shippingForm.city, shipping_zip: shippingForm.zip, shipping_country: shippingForm.country || billingForm.country,
        delivery_email: deliveryEmail,
        billing_type: wantsInvoice ? 'fattura' : 'ricevuta',
        company_name: invoiceForm.company_name,
        vat_number: invoiceForm.vat_number,
        sdi_code: invoiceForm.sdi_code
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setCheckoutLoading(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <BookOpen className="w-12 h-12 mb-4" style={{ color: 'var(--text-muted)' }} />
      <h1 className="text-2xl mb-2" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>Prodotto non disponibile</h1>
      <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>Questo prodotto non e' attualmente visibile.</p>
      <Link to="/"><Button className="rounded-sm" style={{ backgroundColor: 'var(--accent)', color: '#fff' }}><ArrowLeft className="w-4 h-4 mr-2" /> Torna alla Home</Button></Link>
    </div>
  );

  const p = product;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b" style={{ backgroundColor: 'var(--bg-dark)', borderColor: 'rgba(96,165,250,0.15)' }}>
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" style={{ textDecoration: 'none' }}>
            <BookOpen className="w-5 h-5" style={{ color: 'var(--accent-light)' }} />
            <span className="text-lg tracking-tight" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-on-dark)', fontWeight: 600 }}>Quintessenza</span>
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <>
                {user.role === 'admin' && <button onClick={() => navigate('/admin')} className="text-xs px-2 py-1" style={{ color: 'var(--accent-light)' }}><Settings className="w-3.5 h-3.5 inline mr-1" />Admin</button>}
                <button onClick={logout} className="p-1.5" style={{ color: 'var(--text-muted)' }}><LogOut className="w-3.5 h-3.5" /></button>
              </>
            ) : (
              <><button onClick={() => navigate('/auth?mode=login')} className="text-xs px-3 py-1.5" style={{ color: 'var(--accent-light)' }}>Accedi</button>
              <button onClick={() => navigate('/auth?mode=register')} className="text-xs px-4 py-1.5 rounded-sm font-medium" style={{ backgroundColor: 'rgba(59,130,246,0.9)', color: '#fff' }}><User className="w-3.5 h-3.5 inline mr-1" />Registrati</button></>
            )}
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-4">
        <Link to="/" className="inline-flex items-center text-xs transition-colors hover:opacity-70" style={{ color: 'var(--text-muted)', textDecoration: 'none' }} data-testid="back-to-home">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Torna alla Libreria
        </Link>
      </div>

      {/* Product Hero */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-12" data-testid="product-landing-hero">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
          {/* Image */}
          <div className="flex justify-center">
            <div className="relative">
              <img src={p.image_url} alt={p.title} className="w-full max-w-md rounded-sm"
                style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }} data-testid="product-image" />
              {p.badge && (
                <Badge className="absolute top-4 left-4 px-3 py-1.5 text-xs rounded-sm"
                  style={{ backgroundColor: isActive ? 'var(--accent)' : 'var(--espresso)', color: '#fff' }}>{p.badge}</Badge>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="flex flex-col justify-center">
            <p className="text-xs tracking-[0.25em] uppercase mb-3" style={{ color: 'var(--text-muted)' }}>{p.subtitle}</p>
            <h1 className="text-3xl sm:text-4xl tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, color: 'var(--text-primary)' }} data-testid="product-title">
              {p.title}
            </h1>
            <p className="text-sm mb-6 leading-relaxed" style={{ color: 'var(--text-secondary)', fontWeight: 300 }}>{p.description}</p>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-5">
              <span className="text-3xl font-medium" style={{ fontFamily: 'var(--font-heading)', color: isActive ? 'var(--accent)' : 'var(--text-primary)' }}>
                {isActive ? discountedPrice : p.price.toFixed(2)} EUR
              </span>
              {p.compare_at_price && <span className="text-base line-through" style={{ color: 'var(--text-muted)' }}>{p.compare_at_price.toFixed(2)} EUR</span>}
              {isActive && p.promo_discount_value > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-sm" style={{ backgroundColor: 'rgba(37,99,235,0.1)', color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
                  {p.promo_discount_type === 'fixed' ? `-${p.promo_discount_value} EUR` : `-${p.promo_discount_value}%`}
                </span>
              )}
            </div>

            {/* Promo code */}
            {isActive && p.promo_code && (
              <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm mb-5 promo-pulse"
                style={{ backgroundColor: 'rgba(37,99,235,0.08)', border: '1px solid rgba(96,165,250,0.25)' }} data-testid="promo-code-widget">
                <Tag className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} />
                <span className="text-xs" style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>{p.promo_code}</span>
                <button onClick={copyPromo} className="ml-1 p-1 rounded hover:bg-blue-50 transition-colors">
                  {copied ? <Check className="w-3.5 h-3.5" style={{ color: '#10B981' }} /> : <Copy className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} />}
                </button>
              </div>
            )}

            {/* Countdown */}
            {isActive && countdown && (
              <div className="flex items-center gap-1.5 mb-6" data-testid="product-countdown">
                <Clock className="w-3.5 h-3.5 mr-1" style={{ color: 'var(--text-muted)' }} />
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Offerta termina tra</span>
                {[{ val: countdown.days, l: 'g' }, { val: countdown.hours, l: 'h' }, { val: countdown.minutes, l: 'm' }, { val: countdown.seconds, l: 's' }].map(({ val, l }) => (
                  <span key={l} className="px-1.5 py-0.5 rounded-sm text-xs" style={{ backgroundColor: 'rgba(37,99,235,0.08)', fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                    {String(val).padStart(2, '0')}<span style={{ color: 'var(--text-muted)' }}>{l}</span>
                  </span>
                ))}
              </div>
            )}

            {/* CTA */}
            {isActive ? (
              <Button onClick={handleBuyNow} data-testid="product-buy-btn"
                className="cta-glow rounded-sm px-10 py-6 text-base font-medium tracking-wide w-full sm:w-auto"
                style={{ backgroundColor: '#3B82F6', color: '#fff', textShadow: '0 0 10px rgba(255,255,255,0.3)' }}>
                <ShoppingBag className="w-5 h-5 mr-2" /> Acquista Ora
              </Button>
            ) : (
              <Button variant="outline" onClick={() => { trackRedirectToMainSite(campaignId, p.main_site_url); window.open(p.main_site_url, '_blank'); }}
                data-testid="product-main-site-btn" className="rounded-sm px-8 py-5 text-sm w-full sm:w-auto"
                style={{ borderColor: 'var(--border-light)', color: 'var(--text-secondary)' }}>
                <ExternalLink className="w-4 h-4 mr-2" /> Vedi sul Sito Principale
              </Button>
            )}

            {/* Product type */}
            <div className="flex items-center gap-1.5 mt-4">
              {p.is_digital
                ? <><Mail className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} /><span className="text-xs" style={{ color: 'var(--text-muted)' }}>Prodotto digitale</span></>
                : <><Truck className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} /><span className="text-xs" style={{ color: 'var(--text-muted)' }}>Spedizione in 24/48h</span></>
              }
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      {p.video_url && (
        <section className="py-12 border-t" style={{ borderColor: 'var(--border-light)', backgroundColor: 'var(--bg-dark)' }} data-testid="product-video">
          <div className="max-w-4xl mx-auto px-5 md:px-10">
            <h2 className="text-xl tracking-tight mb-6 text-center" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, color: 'var(--text-on-dark)' }}>Video Presentazione</h2>
            <div className="relative w-full rounded-sm overflow-hidden" style={{ paddingBottom: '56.25%', backgroundColor: '#000' }}>
              {p.video_url.includes('youtube.com') || p.video_url.includes('youtu.be') ? (
                <iframe
                  src={`https://www.youtube.com/embed/${p.video_url.includes('youtu.be') ? p.video_url.split('/').pop().split('?')[0] : new URLSearchParams(new URL(p.video_url).search).get('v')}?rel=0`}
                  className="absolute inset-0 w-full h-full" frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen title={p.title} />
              ) : p.video_url.includes('vimeo.com') ? (
                <iframe
                  src={`https://player.vimeo.com/video/${p.video_url.split('/').pop().split('?')[0]}`}
                  className="absolute inset-0 w-full h-full" frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title={p.title} />
              ) : (
                <video controls className="absolute inset-0 w-full h-full" style={{ objectFit: 'contain' }}>
                  <source src={p.video_url} />
                </video>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Long Description + Features */}
      {(p.long_description || features.length > 0) && (
        <section className="py-12 border-t" style={{ borderColor: 'var(--border-light)', backgroundColor: 'var(--bg-white)' }} data-testid="product-details">
          <div className="max-w-3xl mx-auto px-5 md:px-10">
            {p.long_description && (
              <>
                <h2 className="text-xl tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>Descrizione</h2>
                <div className="text-sm leading-relaxed mb-8" style={{ color: 'var(--text-secondary)', fontWeight: 300, whiteSpace: 'pre-line' }}>{p.long_description}</div>
              </>
            )}
            {features.length > 0 && (
              <>
                <h2 className="text-xl tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>Caratteristiche</h2>
                <ul className="space-y-2">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--accent)' }} />
                      {f.trim()}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </section>
      )}

      {/* Social Proof */}
      {socialProof && socialProof.reviews?.length > 0 && (
        <section className="py-12" style={{ backgroundColor: 'var(--bg-warm)' }} data-testid="product-reviews">
          <div className="max-w-5xl mx-auto px-5 md:px-10">
            <div className="flex items-center gap-4 mb-8">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                <span className="text-2xl font-medium" style={{ fontFamily: 'var(--font-heading)' }}>{socialProof.weekly_sales}</span>
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>copie vendute</span>
              </div>
              {socialProof.avg_rating > 0 && (
                <>
                  <div className="w-px h-6" style={{ backgroundColor: 'var(--border-light)' }} />
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4" fill={i <= Math.round(socialProof.avg_rating) ? '#F59E0B' : 'none'} stroke={i <= Math.round(socialProof.avg_rating) ? '#F59E0B' : 'var(--border-light)'} />)}
                    <span className="text-sm ml-1" style={{ color: 'var(--text-primary)' }}>{socialProof.avg_rating}</span>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>({socialProof.total_reviews})</span>
                  </div>
                </>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {socialProof.reviews.slice(0, 4).map((rev, i) => (
                <div key={rev.review_id || i} className="p-5 rounded-sm border" style={{ backgroundColor: 'var(--bg-white)', borderColor: 'var(--border-light)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Quote className="w-4 h-4" style={{ color: 'var(--accent)', opacity: 0.4 }} />
                    <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className="w-3 h-3" fill={s <= rev.rating ? '#F59E0B' : 'none'} stroke={s <= rev.rating ? '#F59E0B' : 'var(--border-light)'} />)}</div>
                  </div>
                  <p className="text-sm mb-3 leading-relaxed" style={{ color: 'var(--text-secondary)', fontWeight: 300 }}>"{rev.text}"</p>
                  <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{rev.user_name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-10" style={{ backgroundColor: 'var(--bg-dark)' }}>
        <div className="max-w-7xl mx-auto px-5 md:px-10 text-center">
          <BookOpen className="w-5 h-5 mx-auto mb-3" style={{ color: 'var(--accent-light)' }} />
          <p className="text-lg tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-on-dark)' }}>Quintessenza Edizioni</p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Selezione editoriale esclusiva</p>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      {isActive && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 px-4 py-3" style={{ backgroundColor: 'var(--bg-dark)', borderTop: '1px solid rgba(96,165,250,0.2)' }}>
          <Button onClick={handleBuyNow} className="w-full rounded-sm py-3 text-sm font-medium cta-glow"
            style={{ backgroundColor: '#3B82F6', color: '#fff' }}>
            <ShoppingBag className="w-4 h-4 mr-2" /> Acquista Ora &middot; {discountedPrice} EUR
          </Button>
        </div>
      )}

      {/* Checkout Dialog - same as Storefront */}
      <Dialog open={checkoutOpen} onOpenChange={(open) => { if (!open) trackAbandonedCart(campaignId, p.title); setCheckoutOpen(open); }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: 'var(--font-heading)' }}>Checkout</DialogTitle>
            <DialogDescription className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {checkoutStep === 1 ? 'Riepilogo e codice promo' : 'Dati di fatturazione e pagamento'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex gap-3 p-3 rounded-sm" style={{ backgroundColor: 'var(--bg-warm)', border: '1px solid var(--border-light)' }}>
              <img src={p.image_url} alt={p.title} className="w-14 h-20 object-cover rounded-sm" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{p.title}</h3>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{p.subtitle}</p>
                <span className="text-sm font-medium" style={{ color: 'var(--accent)' }}>{discountedPrice} EUR</span>
              </div>
            </div>
            {checkoutStep === 1 && (
              <>
                <div>
                  <Label className="text-xs">Codice Promozionale</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={promoInput} onChange={e => setPromoInput(e.target.value)} placeholder="Inserisci codice" className="text-sm" style={{ fontFamily: 'var(--font-mono)' }} />
                    <Button variant="outline" size="sm" onClick={() => { setPromoInput(p.promo_code); toast.info('Codice applicato!'); }} className="text-xs whitespace-nowrap">Applica</Button>
                  </div>
                  {promoInput === p.promo_code && (
                    <p className="text-xs mt-1" style={{ color: 'var(--accent-success)' }}>
                      {p.promo_discount_type === 'fixed'
                        ? `Sconto di ${p.promo_discount_value} EUR applicato!`
                        : `Sconto ${p.promo_discount_value}% applicato!`}
                    </p>
                  )}
                </div>
                <Button onClick={() => setCheckoutStep(2)} className="w-full rounded-sm cta-glow" style={{ backgroundColor: '#3B82F6', color: '#fff' }}>Procedi al Pagamento</Button>
              </>
            )}
            {checkoutStep === 2 && (
              <>
                <div>
                  <p className="text-xs font-medium mb-2 flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" /> Dati di Fatturazione</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2"><Input value={billingForm.name} onChange={e => setBillingForm(f => ({...f, name: e.target.value}))} placeholder="Nome completo" className="text-sm" /></div>
                    <div className="col-span-2"><Input type="email" value={billingForm.email} onChange={e => setBillingForm(f => ({...f, email: e.target.value}))} placeholder="Email" className="text-sm" /></div>
                    <div className="col-span-2">
                      <Input value={billingForm.tax_code} onChange={e => setBillingForm(f => ({...f, tax_code: e.target.value.toUpperCase()}))}
                        placeholder="Codice Fiscale *" maxLength={16} className="text-sm uppercase" data-testid="billing-tax-code" required />
                      <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>Obbligatorio - 16 caratteri persona fisica / 11 cifre P.IVA</p>
                    </div>
                    <div className="col-span-2"><Input value={billingForm.address} onChange={e => setBillingForm(f => ({...f, address: e.target.value}))} placeholder="Indirizzo" className="text-sm" /></div>
                    <Input value={billingForm.city} onChange={e => setBillingForm(f => ({...f, city: e.target.value}))} placeholder="Citta" className="text-sm" />
                    <Input value={billingForm.zip} onChange={e => setBillingForm(f => ({...f, zip: e.target.value}))} placeholder="CAP" className="text-sm" />
                  </div>
                </div>
                {/* Invoice fields - always optional */}
                <div>
                  <p className="text-xs font-medium mb-1">Fatturazione <span className="font-normal" style={{ color: 'var(--text-muted)' }}>(facoltativo)</span></p>
                  <p className="text-[11px] mb-2" style={{ color: 'var(--text-muted)' }}>Compila se hai bisogno della fattura. Lascia vuoto per ricevuta.</p>
                  <div className="grid grid-cols-1 gap-2 p-3 rounded-sm" style={{ backgroundColor: 'var(--bg-warm)', border: '1px solid var(--border-light)' }}>
                    <Input value={invoiceForm.company_name} onChange={e => setInvoiceForm(f => ({...f, company_name: e.target.value}))} placeholder="Ragione Sociale (opz.)" className="text-sm" data-testid="invoice-company" />
                    <Input value={invoiceForm.vat_number} onChange={e => setInvoiceForm(f => ({...f, vat_number: e.target.value}))} placeholder="Partita IVA (opz.)" className="text-sm" data-testid="invoice-vat" />
                    <Input value={invoiceForm.sdi_code} onChange={e => setInvoiceForm(f => ({...f, sdi_code: e.target.value}))} placeholder="Codice Univoco / SDI (opz.)" className="text-sm" data-testid="invoice-sdi" />
                  </div>
                </div>
                {p.is_digital ? (
                  <div>
                    <p className="text-xs font-medium mb-2 flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email di Consegna Digitale</p>
                    <Input type="email" value={deliveryEmail} onChange={e => setDeliveryEmail(e.target.value)} placeholder="Email per ricevere il prodotto" className="text-sm" />
                  </div>
                ) : (
                  <div>
                    <p className="text-xs font-medium mb-2 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Indirizzo di Spedizione</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="col-span-2"><Input value={shippingForm.address} onChange={e => setShippingForm(f => ({...f, address: e.target.value}))} placeholder="Indirizzo spedizione" className="text-sm" /></div>
                      <Input value={shippingForm.city} onChange={e => setShippingForm(f => ({...f, city: e.target.value}))} placeholder="Citta" className="text-sm" />
                      <Input value={shippingForm.zip} onChange={e => setShippingForm(f => ({...f, zip: e.target.value}))} placeholder="CAP" className="text-sm" />
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-xs font-medium mb-2">Metodo di Pagamento</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setPaymentMethod('stripe')} className={`p-3 rounded-sm border text-center ${paymentMethod === 'stripe' ? 'ring-2' : ''}`}
                      style={{ borderColor: paymentMethod === 'stripe' ? 'var(--accent)' : 'var(--border-light)', ringColor: 'var(--accent)' }}>
                      <CreditCard className="w-5 h-5 mx-auto mb-1" style={{ color: paymentMethod === 'stripe' ? 'var(--accent)' : 'var(--text-muted)' }} />
                      <span className="text-xs">Carta</span>
                    </button>
                    <button onClick={() => setPaymentMethod('paypal')} className={`p-3 rounded-sm border text-center ${paymentMethod === 'paypal' ? 'ring-2' : ''}`}
                      style={{ borderColor: paymentMethod === 'paypal' ? '#0070BA' : 'var(--border-light)', ringColor: '#0070BA' }}>
                      <span className="text-lg font-bold block mb-0.5" style={{ color: paymentMethod === 'paypal' ? '#0070BA' : 'var(--text-muted)' }}>PP</span>
                      <span className="text-xs">PayPal</span>
                    </button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setCheckoutStep(1)} className="flex-1 rounded-sm text-sm">Indietro</Button>
                  <Button onClick={handleCheckout} disabled={checkoutLoading} className="flex-1 rounded-sm cta-glow text-sm"
                    style={{ backgroundColor: paymentMethod === 'paypal' ? '#0070BA' : '#3B82F6', color: '#fff' }}>
                    {checkoutLoading ? 'Elaborazione...' : paymentMethod === 'paypal' ? 'Paga con PayPal' : 'Paga con Carta'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
